import React, { JSX, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { RegistrationResponse } from 'services/api-client';
import { RegistrationSuccessDialog } from 'components/EmailVerificationComponents';
import { useAuth } from 'hooks/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LoadingLabel, PasswordField } from './RegistrationFields';
import { CandidateRegistrationData } from './registrationTypes';
import { resolveRegistrationError } from './registrationErrors';
import { validateCandidateRegistration } from './registrationValidation';

export const CandidateRegistrationForm = (): JSX.Element => {
  const { apiClient } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState<CandidateRegistrationData>({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [registrationResult, setRegistrationResult] = useState<RegistrationResponse | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (field: keyof CandidateRegistrationData, value: string): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (): Promise<void> => {
    const validationErrors = validateCandidateRegistration(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length !== 0) return;

    setLoading(true);
    try {
      const result = await apiClient.createCandidate({
        email: formData.email,
        username: formData.username,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || undefined,
      });
      apiClient.setPendingEmailVerification(formData.email);
      setRegistrationResult(result);
      setShowSuccess(true);
    } catch (error) {
      setErrors(resolveRegistrationError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 5 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          Join as a Candidate
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create your account to start finding your dream job
        </Typography>
      </Box>
      <Stack spacing={3}>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={e => handleInputChange('email', e.target.value)}
          placeholder="your.email@example.com"
          error={!!errors.email}
          helperText={errors.email}
          required
        />
        <TextField
          fullWidth
          label="Username"
          value={formData.username}
          onChange={e => handleInputChange('username', e.target.value.toLowerCase())}
          placeholder="johndoe123"
          error={!!errors.username}
          helperText={errors.username}
          required
        />
        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            label="First Name"
            value={formData.firstName}
            onChange={e => handleInputChange('firstName', e.target.value)}
            placeholder="John"
            error={!!errors.firstName}
            helperText={errors.firstName}
            required
          />
          <TextField
            fullWidth
            label="Last Name"
            value={formData.lastName}
            onChange={e => handleInputChange('lastName', e.target.value)}
            placeholder="Doe"
            error={!!errors.lastName}
            helperText={errors.lastName}
            required
          />
        </Stack>
        <TextField
          fullWidth
          label="Phone Number"
          type="tel"
          value={formData.phone}
          onChange={e => handleInputChange('phone', e.target.value)}
          placeholder="+1 (555) 123-4567"
          error={!!errors.phone}
          helperText={errors.phone || 'Optional'}
        />
        <PasswordField
          label="Password"
          value={formData.password}
          error={errors.password}
          placeholder="Create a strong password"
          showPassword={showPassword}
          showStrength
          onToggle={() => setShowPassword(!showPassword)}
          onChange={value => handleInputChange('password', value)}
        />
        <PasswordField
          label="Confirm Password"
          value={formData.confirmPassword}
          error={errors.confirmPassword}
          placeholder="Confirm your password"
          showPassword={showConfirmPassword}
          onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
          onChange={value => handleInputChange('confirmPassword', value)}
        />
        {errors.general && <Alert severity="error">{errors.general}</Alert>}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ py: 2 }}
        >
          {loading ? <LoadingLabel text="Creating Account..." /> : 'Create Account'}
        </Button>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Link
              component="button"
              onClick={e => {
                e.preventDefault();
                navigate('/login');
              }}
              sx={{ fontWeight: 600 }}
            >
              Sign in here
            </Link>
          </Typography>
        </Box>
      </Stack>
      {showSuccess && registrationResult && (
        <RegistrationSuccessDialog
          open={showSuccess}
          onClose={() => setShowSuccess(false)}
          email={registrationResult.email}
          userType="candidate"
        />
      )}
    </Box>
  );
};
